/* PostHog analytics for VerifAEye AI predictions.   Two events power the model-quality dashboard:
     verifaeye_inference — fired when the AI makes a prediction
     verifaeye_feedback  — fired when the user accepts, corrects or rejects it
   confidence_score is always a 0-1 float. Timing is reported as two separate numbers:
   model_latency_ms (real AI model time, from the server) and round_trip_latency_ms (the full wait
   the user experienced), plus overhead_ms for the difference. */

import axios from 'axios';

const ph = () => (typeof window !== 'undefined' ? window.posthog : null);

const clean = (props) =>
  Object.fromEntries(Object.entries(props).filter(([, v]) => v !== undefined));

/** An AI request that keeps the user waiting longer than this is flagged as slow. */
export const SLOW_REQUEST_THRESHOLD_MS = 30000;
/** Predictions below this certainty get a warning and a retake offer. */
export const LOW_CONFIDENCE_THRESHOLD = 0.5;

const capture = (event, props) => {
  const client = ph();
  if (!client?.capture) return;
  try {
    client.capture(event, clean(props));
  } catch (e) {
    console.warn('analytics capture failed', e);
  }
};

/** AI confidence arrives as 1-10 from the vision model; PostHog wants a 0-1 float. */
export const toConfidence = (score) => {
  if (score === null || score === undefined || score === '') return null;
  const n = Number(score);
  if (Number.isNaN(n)) return null;
  const fraction = n > 1 ? n / 10 : n;
  return Math.round(Math.min(Math.max(fraction, 0), 1) * 100) / 100;
};

/** Milliseconds between two ISO timestamps, for async flows where the user waits on a queue. */
export const msBetween = (startIso, endIso) => {
  if (!startIso || !endIso) return undefined;
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  return Number.isFinite(ms) && ms >= 0 ? Math.round(ms) : undefined;
};

const timing = (model_latency_ms, round_trip_latency_ms) => {
  const model = model_latency_ms === null || model_latency_ms === undefined ? null : Math.round(Number(model_latency_ms));
  const roundTrip = round_trip_latency_ms === null || round_trip_latency_ms === undefined ? undefined : Math.round(Number(round_trip_latency_ms));
  const waited = roundTrip !== undefined ? roundTrip : model;
  return {
    // the real AI model time, measured on the server
    model_latency_ms: model,
    // the full wait the user experienced, measured in the browser (or queue time for async flows)
    round_trip_latency_ms: roundTrip,
    // everything that was not the model: upload, network, queueing
    overhead_ms: roundTrip !== undefined && model !== null ? Math.max(roundTrip - model, 0) : undefined,
    is_slow: waited !== null && waited !== undefined ? waited >= SLOW_REQUEST_THRESHOLD_MS : undefined,
    // deprecated alias kept so existing saved PostHog queries on latency_ms keep working
    latency_ms: model,
  };
};

/* PRIVACY: never pass emails, names, item descriptions, inventory contents or images
   into any property below. Categories, rooms, counts and IDs only. */
let identifiedId = null;

export const identifyUser = (user) => {
  const client = ph();
  if (!client?.identify || !user?.id || identifiedId === user.id) return;
  identifiedId = user.id;
  try {
    client.identify(user.id, {
      email_verified: !!user.email_verified,
      tier: user.tier || user.plan || undefined,
    });
  } catch (e) {
    console.warn('analytics identify failed', e);
  }
};

/* ---- Sign-up / activation funnel ---- */

/** Fires when the register page opens, so 'opened /register but never submitted' is measurable. */
export const captureSignupViewed = () => capture('signup_viewed', {});

/** method: 'email' | 'google' */
export const captureSignupStarted = (method) => capture('signup_started', { method });
export const captureSignupCompleted = (method) => capture('signup_completed', { method });
export const captureEmailVerified = () => capture('email_verified', {});
export const captureLogin = (method) => capture('login', { method });

let exhaustedFired = false;

/** Fired when a user with no paid credits hits zero free analyses. Only ever sent once per page session. */
export const captureFreeCreditsExhausted = ({ usage, surface }) => {
  if (exhaustedFired || (usage && usage.is_free_tier === false)) return;
  exhaustedFired = true;
  capture('free_credits_exhausted', {
    surface,
    analyses_limit: usage?.analyses_limit,
    analyses_used: usage?.analyses_used,
  });
};

/** Fires analysis_run for every AI analysis, plus free_credits_exhausted the moment the free allowance runs out. */
export const captureAnalysisRun = ({ usage, surface, photos = 1 }) => {
  const remaining = usage?.analyses_remaining;
  const isFree = usage ? !!usage.is_free_tier : undefined;
  capture('analysis_run', {
    surface,
    photos,
    free_or_paid: isFree === undefined ? undefined : isFree ? 'free' : 'paid',
    credits_remaining: remaining,
  });
  if (isFree && remaining === 0) captureFreeCreditsExhausted({ usage, surface });
};

/** How a photo got into the app: the in-app camera, the phone's own camera app, or existing files.
 *  Fires both the combined event and the per-source one, so either reads cleanly in PostHog. */
export const captureImageAdded = ({ source, count = 1, method, surface = 'inventory' }) => {
  const props = { source, count, method, surface };
  capture('image_added', props);
  capture(source === 'camera' ? 'image_captured' : 'image_uploaded', props);
};

/** price is the display amount in AUD dollars, e.g. 6.99 */
export const captureCheckoutStarted = ({ pack_id, pack_name, price, currency = 'AUD' }) =>
  capture('checkout_started', { pack_id, pack_name, price, currency });

export const resetAnalytics = () => {
  exhaustedFired = false;
  identifiedId = null;
  try {
    ph()?.reset?.();
  } catch (e) {
    console.warn('analytics reset failed', e);
  }
};

/**
 * @param surface  where the prediction happened: item_photo | background_photo | receipt | claim_photo | bank_statement
 * @param context  pre_loss | post_loss
 */
/**
 * Fires a dedicated event you can alert on in PostHog, and records the request server-side
 * so it shows up in the slow-request list. Safe to call on every inference — it no-ops when fast.
 */
export const reportIfSlow = async ({ model_latency_ms, round_trip_latency_ms, model, surface, context = 'pre_loss', predicted_outcome, claim_id, apiUrl, token, report }) => {
  const waited = round_trip_latency_ms ?? model_latency_ms;
  if (!waited || waited < SLOW_REQUEST_THRESHOLD_MS) return false;

  capture('verifaeye_slow_inference', {
    ...timing(model_latency_ms, round_trip_latency_ms),
    threshold_ms: SLOW_REQUEST_THRESHOLD_MS,
    model: model || null,
    surface,
    context,
    predicted_outcome: predicted_outcome || null,
    claim_id,
  });

  const payload = {
    surface,
    context,
    model: model || null,
    model_latency_ms: model_latency_ms ?? null,
    round_trip_latency_ms: round_trip_latency_ms ?? null,
    predicted_outcome: predicted_outcome || null,
    claim_id: claim_id || null,
  };

  try {
    if (report) {
      await report(payload);
    } else if (apiUrl && token) {
      await axios.post(`${apiUrl}/api/analytics/slow-request`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }
  } catch (e) {
    console.warn('slow request not recorded', e);
  }
  return true;
};

export const captureInference = ({
  predicted_outcome,
  confidence_score,
  model_latency_ms,
  round_trip_latency_ms,
  model,
  surface,
  context = 'pre_loss',
  room,
  category,
  items_found,
  claim_id,
  photo_id,
}) =>
  capture('verifaeye_inference', {
    predicted_outcome: predicted_outcome || 'uncategorized',
    confidence_score: toConfidence(confidence_score),
    ...timing(model_latency_ms, round_trip_latency_ms),
    model: model || null,
    surface,
    context,
    room: room || '',
    category: category || predicted_outcome || '',
    items_found,
    claim_id,
    photo_id,
  });

/**
 * was_accepted is true ONLY when the user confirmed the prediction without changing anything.
 * corrected_outcome is what they changed it to (null when accepted untouched).
 * feedback_source: confirm | thumbs | removed | deleted | dismissed
 */
export const captureFeedback = ({
  was_accepted,
  predicted_outcome,
  corrected_outcome = null,
  confidence_score,
  model_latency_ms,
  round_trip_latency_ms,
  model,
  surface,
  context = 'pre_loss',
  room,
  fields_changed,
  feedback_source = 'confirm',
  item_id,
  claim_id,
  rating,
}) =>
  capture('verifaeye_feedback', {
    was_accepted: !!was_accepted,
    predicted_outcome: predicted_outcome || 'uncategorized',
    corrected_outcome: corrected_outcome || null,
    confidence_score: toConfidence(confidence_score),
    ...timing(model_latency_ms, round_trip_latency_ms),
    model: model || null,
    surface,
    context,
    room: room || '',
    fields_changed,
    feedback_source,
    item_id,
    claim_id,
    rating,
  });
